import React from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { getProductData, ProductKey } from '@/components/signup/utils/products';
import { wacToken } from '@/libraries/design/foundations';

import { servcoTemplateMessages } from '../servco-template-messages';
import { productKeyAttributes } from '../template-definitions';

import Card, { type CardProps } from './card';

export interface CardWithFooterProps extends CardProps {
	/** Product that powers the template. Undefined means Jira. */
	productKey?: ProductKey;
}

const poweredByContainerStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: token('space.050'),
	marginBlockStart: token('space.100'),
});

const poweredByProductWrapperStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: token('space.050'),
});

const poweredByTextStyle = css({
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '12px',
	lineHeight: '16px',
	color: token('color.text.subtlest'),
	margin: 0,
});

const poweredByProductStyle = css({
	fontWeight: token('font.weight.semibold'),
	color: token('color.text.subtle'),
});

const PoweredBySection: React.FC<{ productKey: ProductKey }> = ({ productKey }) => {
	const Icon = productKeyAttributes[productKey]?.icon;
	const { productName } = getProductData(productKey);

	return (
		<div css={poweredByContainerStyle}>
			<span css={poweredByTextStyle}>
				<FormattedMessage {...servcoTemplateMessages.poweredByText} />
			</span>
			<div css={poweredByProductWrapperStyle}>
				{Icon && <Icon appearance="brand" size="xsmall" shouldUseNewLogoDesign label="" />}
				{productName && (
					<span css={[poweredByTextStyle, poweredByProductStyle]}>{productName}</span>
				)}
			</div>
		</div>
	);
};

/**
 * Template card used in the JSMGROWTH-583 ServCo variation. Adds a non-interactive
 * "Powered by <product>" footer so users can tell Jira templates from JSM / CSM ones.
 */
const CardWithFooter: React.FC<CardWithFooterProps> = ({
	productKey: servCoProductKey,
	...cardProps
}) => {
	const productKey = servCoProductKey ?? ProductKey.JIRA_SOFTWARE;

	return (
		<Card
			{...cardProps}
			analyticsAttributes={{
				productKey,
				isServCoOption: servCoProductKey !== undefined,
			}}
			footer={<PoweredBySection productKey={productKey} />}
		/>
	);
};

export default CardWithFooter;
